import React, { useState } from "react";
import { createTicket, presignAttachmentUpload, uploadAttachmentToS3 } from "../api/ticketApi";
import { useI18n } from "../i18n";
import "./SupportTicketModal.css";

function SupportTicketModal({ isOpen, onClose }) {
  const { t } = useI18n();
  const TICKET_TYPES = [
    { value: "CONTENT_ISSUE", label: t("support.contentIssue") },
    { value: "PAYMENT_ISSUE", label: t("support.paymentIssue") },
    { value: "ACCOUNT_ISSUE", label: t("support.accountIssue") },
    { value: "BUG_REPORT", label: t("support.bugReport") },
    { value: "ABUSE_REPORT", label: t("support.abuseReport") },
    { value: "OTHER", label: t("support.other") }
  ];

  const [ticketType, setTicketType] = useState("BUG_REPORT");
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) {
    return null;
  }

  function handleTypeChange(event) {
    setTicketType(event.target.value);
    setError("");
    setSuccessMessage("");
  }

  function handleMessageChange(event) {
    setMessage(event.target.value);
    setError("");
    setSuccessMessage("");
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (10MB max, matching backend)
      if (file.size > 10 * 1024 * 1024) {
        setError(t("support.fileSize"));
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError("");
      setSuccessMessage("");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!ticketType) {
      setError(t("support.selectType"));
      return;
    }

    if (!message || message.trim().length === 0) {
      setError(t("support.enterMessage"));
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccessMessage("");

      const attachments = [];

      // Upload file if selected
      if (selectedFile) {
        const presignData = await presignAttachmentUpload(
          selectedFile.name,
          selectedFile.type,
          selectedFile.size
        );

        await uploadAttachmentToS3(presignData.uploadUrl, selectedFile);

        attachments.push({
          attachmentId: presignData.attachmentId,
          s3Key: presignData.s3Key,
          fileName: selectedFile.name,
          contentType: selectedFile.type,
          fileSize: selectedFile.size
        });
      }

      // Create ticket
      const response = await createTicket(ticketType, message, attachments);

      const typeLabel = TICKET_TYPES.find(t => t.value === ticketType)?.label || ticketType;

      setSuccessMessage(
        t("support.success", {
          type: typeLabel,
          id: response.id.substring(0, 8)
        })
      );

      // Reset form
      setMessage("");
      setTicketType("BUG_REPORT");
      setSelectedFile(null);

      // Close after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error("Create ticket error:", err);

      if (err.response && err.response.status === 401) {
        setError(t("support.sessionExpired"));
      } else if (err.response && err.response.data) {
        setError(
          typeof err.response.data === "string"
            ? err.response.data
            : err.response.data.message || t("support.createError")
        );
      } else {
        setError(t("support.createError"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setMessage("");
    setTicketType("BUG_REPORT");
    setSelectedFile(null);
    setError("");
    setSuccessMessage("");
    onClose();
  }

  return (
    <div className="support-ticket-overlay">
      <div className="support-ticket-modal">
        <button
          type="button"
          className="support-ticket-close"
          onClick={handleClose}
          aria-label={t("support.close")}
        >
          ×
        </button>

        <h2 className="support-ticket-title">{t("support.title")}</h2>

        <p className="support-ticket-description">
          {t("support.description")}
        </p>

        <form onSubmit={handleSubmit} className="support-ticket-form">
          <label className="support-ticket-label" htmlFor="ticketType">
            {t("support.issueType")}
          </label>

          <select
            id="ticketType"
            value={ticketType}
            onChange={handleTypeChange}
            className="support-ticket-select"
            disabled={submitting}
          >
            {TICKET_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          <label className="support-ticket-label" htmlFor="message">
            {t("support.describe")}
          </label>

          <textarea
            id="message"
            value={message}
            onChange={handleMessageChange}
            placeholder={t("support.placeholder")}
            className="support-ticket-textarea"
            disabled={submitting}
            rows={5}
          />

          <label className="support-ticket-label" htmlFor="file">
            {t("support.attach")}
          </label>

          <input
            id="file"
            type="file"
            onChange={handleFileChange}
            className="support-ticket-file-input"
            disabled={submitting}
            accept="image/*,.pdf,.doc,.docx"
          />

          {selectedFile && (
            <div className="support-ticket-file-preview">
              <span>✓ {selectedFile.name}</span>
              <button
                type="button"
                className="support-ticket-file-remove"
                onClick={() => setSelectedFile(null)}
              >
                ✕
              </button>
            </div>
          )}

          {error && (
            <div className="support-ticket-error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="support-ticket-success">
              {successMessage}
            </div>
          )}

          <div className="support-ticket-actions">
            <button
              type="submit"
              className="support-ticket-submit"
              disabled={submitting}
            >
              {submitting ? t("support.sending") : t("support.submit")}
            </button>

            <button
              type="button"
              className="support-ticket-cancel"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("support.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SupportTicketModal;
